import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { Product } from '../../../types';
import { API_URL, CUSTOMER_API_URL, PARTNER_ID } from '../../../config';

import { UpsellProduct } from '../../upsells/types';

/**
 * Query argument for getProduct - can be just a string (productTag)
 * or an object with productTag and optional partnerId
 */
export type GetProductQueryArg =
  | string
  | { productTag: string; partnerId?: string; refersionId?: string };

export type GetEmbedProductsQueryArg = void | {
  partnerId?: string;
  refersionId?: string;
};

// Single API slice object.
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({ baseUrl: CUSTOMER_API_URL || API_URL }),
  endpoints: builder => ({
    getProduct: builder.query<Product, GetProductQueryArg>({
      query: arg => {
        // Support both string and object arguments for backwards compatibility
        const productTag = typeof arg === 'string' ? arg : arg.productTag;
        const refersionId =
          typeof arg === 'string' ? undefined : arg.refersionId;

        // Resolve partnerId: explicit arg takes priority, then URL param (covers
        // child components whose useEffect fires before the parent Configurator's),
        // then the configured PARTNER_ID constant.
        const urlPartnerId =
          new URLSearchParams(window.location.search).get('partner_id') ??
          undefined;
        const explicitPartnerId =
          typeof arg === 'string' ? undefined : arg.partnerId;
        const partnerId = explicitPartnerId ?? urlPartnerId ?? PARTNER_ID;

        // Build URL with optional partner_id/refersion_id for partner product support
        const baseUrl = `/products/${productTag}`;
        const params = new URLSearchParams();

        if (partnerId) {
          params.set('partner_id', partnerId);
        } else if (refersionId) {
          params.set('refersion_id', refersionId);
        }

        const queryString = params.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
      },
      // Keep just productTag as the cache key so updateStyle then can find
      // cached data using apiSlice.endpoints.getProduct.select(productTag).
      serializeQueryArgs: ({ queryArgs }) => {
        if (!queryArgs) return '';
        return typeof queryArgs === 'string' ? queryArgs : queryArgs.productTag;
      },
      transformResponse: async (response: { url: string }) => {
        // Return the data loaded from S3
        const res = await fetch(response.url);
        const data = await res.json();
        return data;
      }
    }),

    getUpsells: builder.query<UpsellProduct[], string>({
      query: productId => `/products/upsells/${productId}`,
      transformResponse: async (response: { url: string }) => {
        // Return the data loaded from S3
        const res = await fetch(response.url);
        const data = await res.json();
        return data;
      }
    }),

    // TODO: Create Proper Typing For This Object
    getEmbedProducts: builder.query<
      Array<{ product_tag: string; display_name: string; product_id: string }>,
      GetEmbedProductsQueryArg
    >({
      query: arg => {
        const params = new URLSearchParams();
        const partnerId = arg && 'partnerId' in arg ? arg.partnerId : undefined;
        const refersionId =
          arg && 'refersionId' in arg ? arg.refersionId : undefined;

        if (partnerId) {
          params.set('partner_id', partnerId);
        } else if (refersionId) {
          params.set('refersion_id', refersionId);
        }

        const queryString = params.toString();
        return queryString
          ? `/products/embed-list?${queryString}`
          : `/products/embed-list`;
      }
    })
  })
});

export const {
  useGetProductQuery,
  useGetUpsellsQuery,
  useGetEmbedProductsQuery
} = apiSlice;
